import React, { useEffect, useState } from 'react';
import { useAlert } from 'react-alert';
import { useTranslation } from 'react-i18next';
import SkeletonLoader from 'shared/components/ui/SkeletonLoader';
import { getError } from 'utils/appHelpers';
import { API, graphqlOperation } from 'aws-amplify';
import { searchUsers } from 'graphql/user/queries';
import { searchProjects } from 'graphql/projects/queries';
import { searchLikieesQuery } from 'graphql/likiees/queries';
import { useSelector } from 'react-redux';
import SearchPersons from './components/SearchPersons';
import SearchProjects from './components/SearchProjects';
import SearchLikiees from './components/SearchLikiees';
import Button from 'shared/components/Button';
import Input from 'shared/components/Input';
import useDebounce from 'shared/hooks/useDebounce';

const Search = ({ location }) => {
  const { t } = useTranslation();
  const alert = useAlert();
  const [fetching, setFetching] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [users, setUsers] = useState();
  const [projects, setProjects] = useState();
  const [likiees, setLikiees] = useState();
  const [localSearch, setLocalSearch] = useState('');
  const search = new URLSearchParams(location.search).get('q');
  const account = useSelector(state => state.account);
  const localDebSearch = useDebounce(localSearch, 300);

  const onFormChange = e => {
    setLocalSearch(e.target.value);
  };

  const searchData = async () => {
    try {
      setFetching(true);
      const searchText = localSearch || search;
      const usersBody = {
        or: [{ name: { matchPhrase: searchText } }, { email: { matchPhrasePrefix: searchText } }],
        id: { ne: account.id },
      };
      const usersSearch = await API.graphql(graphqlOperation(searchUsers, { filter: usersBody }));
      setUsers(usersSearch.data.searchUsers.items);

      const projectsBody = { name: { matchPhrasePrefix: searchText } };
      const projectsSearch = await API.graphql(graphqlOperation(searchProjects, { filter: projectsBody }));
      setProjects(projectsSearch.data.searchProjects.items);

      const likiiesBody = {
        or: [
          { name: { matchPhrase: searchText } },
          { description: { matchPhrase: searchText } },
          { keywords: { matchPhrase: searchText } },
        ],
      };
      const likiiesSearch = await API.graphql(graphqlOperation(searchLikieesQuery, { filter: likiiesBody }));
      setLikiees(likiiesSearch.data.searchLikiees.items);
    } catch (err) {
      alert.error(getError(err));
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    if (search || localDebSearch) searchData();
  }, [search, localDebSearch]);

  const tabs = [
    { name: t('tab_all'), state: 'all' },
    { name: t('tab_likiees'), state: 'likiees' },
    { name: t('tab_profiles'), state: 'profile' },
    { name: t('tab_folders'), state: 'folders' },
  ];

  const showAllTabs = activeTab === 'all';

  return (
    <div className='products-search-page pb-10'>
      <div className='d-md-none'>
        <Input
          type='text'
          name='topSearch'
          onChange={onFormChange}
          value={localSearch}
          placeholder={t('ph_search')}
          className='mb-0 w-100'
          inputClassName='mb-10'
        />
      </div>
      <div className='mb-15'>
        {tabs.map(item => {
          return (
            <Button
              className={`mr-md-8 mr-5 py-4 px-md-10 px-8 fw-300 btn-reboot ${
                activeTab === item.state ? 'btn-primary' : 'btn-gray-light'
              }`}
              onClick={() => setActiveTab(item.state)}
              key={item.state}
            >
              {item.name}
            </Button>
          );
        })}
      </div>
      {fetching && <SkeletonLoader type='list-items' count={5} />}
      {!fetching && (
        <>
          {(activeTab === 'profile' || showAllTabs) && (
            <SearchPersons showAllTabs={showAllTabs} items={users} onClickSeeMore={() => setActiveTab('profile')} />
          )}
          {(activeTab === 'folders' || showAllTabs) && (
            <SearchProjects showAllTabs={showAllTabs} items={projects} onClickSeeMore={() => setActiveTab('folders')} />
          )}
          {(activeTab === 'likiees' || showAllTabs) && <SearchLikiees showAllTabs={showAllTabs} items={likiees} />}
        </>
      )}
    </div>
  );
};

export default Search;
